import {
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import { createSaleOrderWithLots } from "../../../utils/accessoryLotInventory";
import { ANNUAL_MASK_STATUS, canAdministerAnnualMaskPurchase, getAnnualMaskSelectedSaleName, isAnnualMaskReceiptDateValid } from "./annualMaskPurchaseUtils";

const safeText = (value) =>
  value === undefined || value === null ? "" : String(value).trim();

const getActorName = (currentUser) =>
  safeText(currentUser && (currentUser.displayName || currentUser.email));

const getPatientName = (patient = {}) =>
  [
    patient.firstnameTh || patient.firstname,
    patient.lastnameTh || patient.lastname,
  ]
    .map(safeText)
    .filter(Boolean)
    .join(" ");

export const getAnnualMaskSaleOrderDocumentId = (caseItem = {}) => {
  const batchId = safeText(caseItem.batchId);
  const caseId = safeText(caseItem.id);
  if (!batchId || !caseId) {
    throw new Error("Annual Mask Purchase batch and case IDs are required.");
  }

  return `annual-mask-${encodeURIComponent(batchId)}-${encodeURIComponent(
    caseId,
  )}`;
};

export const reserveAnnualMaskSaleOrderNumber = async (
  database,
  now = new Date(),
) => {
  const sequenceRef = doc(database, "sequenceTracker", "saleOrderSequence");
  return runTransaction(database, async (transaction) => {
    const snapshot = await transaction.get(sequenceRef);
    const thaiYear = now.getFullYear() + 543;
    const year = String(thaiYear).slice(-2);
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const stored = snapshot.exists() ? snapshot.data() : {};
    const samePeriod =
      Number(stored.year) === thaiYear &&
      String(stored.month).padStart(2, "0") === month;
    const next =
      samePeriod && Number(stored.current) > 0 ? Number(stored.current) : 1;

    transaction.set(
      sequenceRef,
      {
        current: next + 1,
        year: thaiYear,
        month,
      },
      { merge: true },
    );
    return `IGC${year}${month}${String(next).padStart(3, "0")}`;
  });
};

export const buildAnnualMaskSaleOrderData = ({
  caseItem = {},
  mask = {},
  receiptNo,
  currentUser,
  now = new Date(),
}) => {
  const patient = caseItem.patient || {};
  const selectedMask = caseItem.selectedMask || {};
  const selectedSaleName = getAnnualMaskSelectedSaleName(caseItem);
  const actor = getActorName(currentUser);
  const hn = safeText(caseItem.hn);

  if (!hn) throw new Error("Patient HN is required before creating the order.");
  if (!safeText(receiptNo)) throw new Error("Sale order number is required.");
  if (!selectedSaleName) {
    throw new Error("Select a Sales Representative before marking ready.");
  }
  if (!safeText(mask.id || selectedMask.accessoryId)) {
    throw new Error("Select the approved mask type before marking ready.");
  }

  if (!isAnnualMaskReceiptDateValid(caseItem.maskReceiptDate)) {
    throw new Error("Save the expected mask receipt date before marking ready.");
  }

  const item = {
    productId: safeText(mask.id || selectedMask.accessoryId),
    productName: safeText(mask.itemName || selectedMask.itemName),
    productSn: safeText(mask.itemCode || selectedMask.itemCode),
    price: Number(mask.unitPrice) || Number(selectedMask.unitPrice) || 0,
    quantity: 1,
    size: safeText(mask.size || selectedMask.size),
    productBrand: safeText(mask.brand || selectedMask.brand),
    productType: safeText(mask.category) || "Mask",
    itemType: "Accessory",
  };

  return {
    receiptNo: safeText(receiptNo),
    hn,
    firstname: patient.firstnameTh || patient.firstname || "",
    lastname: patient.lastnameTh || patient.lastname || "",
    items: [item],
    totalAmount: item.price,
    receiptDate: now,
    status: "เตรียมเครื่อง",
    sales: selectedSaleName,
    maskReceiptDate: caseItem.maskReceiptDate,
    salePackage: "Annual 1 Year Mask Purchase",
    activity: "annual_mask_purchase",
    note: `Annual Mask Purchase batch: ${
      caseItem.batchName || caseItem.batchId
    }`,
    refCustomer: patient.refCustomer || "",
    refHospital: patient.refHn || "",
    nodeRef: patient.nodeRef || "",
    mobile: patient.mobile || "",
    accessoryPackageStatus: {},
    annualMaskBatchId: caseItem.batchId,
    annualMaskCaseId: caseItem.id,
    annualMaskCasePath: caseItem.refPath,
    annualMaskSourceFollowupPath: caseItem.sourceFollowupPath || "",
    createdAt: now,
    createdBy: actor,
    updatedAt: now,
    updatedBy: actor,
  };
};

const isMatchingAnnualMaskOrder = (orderData = {}, caseItem = {}) =>
  safeText(orderData.annualMaskCasePath) === safeText(caseItem.refPath) &&
  safeText(orderData.annualMaskBatchId) === safeText(caseItem.batchId) &&
  safeText(orderData.annualMaskCaseId) === safeText(caseItem.id);

export const createAnnualMaskSaleOrderWithStock = async ({
  database,
  caseItem,
  mask,
  currentUser,
}) => {
  const orderId = getAnnualMaskSaleOrderDocumentId(caseItem);
  const orderRef = doc(database, "saleOrders", orderId);
  const existingSnapshot = await getDoc(orderRef);

  if (existingSnapshot.exists()) {
    const existingOrder = existingSnapshot.data();
    if (!isMatchingAnnualMaskOrder(existingOrder, caseItem)) {
      throw new Error("The reserved Annual Mask Sale Order ID is already in use.");
    }
    return {
      orderRef,
      orderData: existingOrder,
      receiptNo: safeText(existingOrder.receiptNo),
      alreadyExisted: true,
    };
  }

  const now = new Date();
  const receiptNo = await reserveAnnualMaskSaleOrderNumber(database, now);
  const orderData = buildAnnualMaskSaleOrderData({
    caseItem,
    mask,
    receiptNo,
    currentUser,
    now,
  });

  try {
    await createSaleOrderWithLots(
      database,
      orderData,
      currentUser,
      orderRef,
    );
    const createdSnapshot = await getDoc(orderRef);
    return {
      orderRef,
      orderData: createdSnapshot.exists() ? createdSnapshot.data() : orderData,
      receiptNo,
      alreadyExisted: false,
    };
  } catch (error) {
    // If two clients click Ready at nearly the same time, the deterministic
    // order ID lets the second client recover the order without consuming
    // mask stock twice.
    const recoveredSnapshot = await getDoc(orderRef);
    if (recoveredSnapshot.exists()) {
      const recoveredOrder = recoveredSnapshot.data();
      if (isMatchingAnnualMaskOrder(recoveredOrder, caseItem)) {
        return {
          orderRef,
          orderData: recoveredOrder,
          receiptNo: safeText(recoveredOrder.receiptNo),
          alreadyExisted: true,
        };
      }
    }
    throw error;
  }
};

export const getAnnualMaskOrderPatientLabel = (caseItem = {}) =>
  getPatientName(caseItem.patient || caseItem) || safeText(caseItem.hn);

// Complete both ends of the link atomically; stock was reserved when the order
// was created, so completion must not run inventory allocation again.
export const completeAnnualMaskSaleOrder = async ({ database, caseItem, currentUser }) => {
  if (!canAdministerAnnualMaskPurchase(currentUser)) throw new Error("Only an administrator can complete this purchase.");
  if (!caseItem.refPath) throw new Error("Annual Mask Purchase case is unavailable.");
  return runTransaction(database, async (transaction) => {
    const caseRef = doc(database, caseItem.refPath);
    const caseSnapshot = await transaction.get(caseRef);
    if (!caseSnapshot.exists()) throw new Error("Annual Mask Purchase case no longer exists.");
    const currentCase = caseSnapshot.data();
    if (!currentCase.saleOrderId) throw new Error("Create the Sale Order before completing this purchase.");
    const orderRef = doc(database, "saleOrders", currentCase.saleOrderId);
    const orderSnapshot = await transaction.get(orderRef);
    if (!orderSnapshot.exists()) throw new Error("The linked Sale Order no longer exists.");
    const order = orderSnapshot.data();
    if (order.annualMaskCasePath !== caseRef.path || safeText(order.hn) !== safeText(currentCase.hn)) {
      throw new Error("The Sale Order does not match this Annual Mask Purchase case.");
    }
    const sourcePath = currentCase.sourceFollowupPath || order.annualMaskSourceFollowupPath;
    const sourceRef = sourcePath ? doc(database, sourcePath) : null;
    const sourceSnapshot = sourceRef ? await transaction.get(sourceRef) : null;
    const sourceLink = sourceSnapshot && sourceSnapshot.exists() &&
      (sourceSnapshot.data().extendedProps || {}).annualMaskPurchase;
    const isCurrentLink = sourceLink && (sourceLink.casePath === caseRef.path ||
      (order.annualMaskBatchId && order.annualMaskCaseId && sourceLink.batchId === order.annualMaskBatchId && sourceLink.caseId === order.annualMaskCaseId));
    const mirrorRefs = [];
    if (order.hn) mirrorRefs.push(doc(database, "patients", order.hn, "orders", currentCase.saleOrderId));
    (order.items || []).filter((item) => item.productType === "CPAP" && item.productId).forEach((item) => {
      mirrorRefs.push(doc(database, "products", item.productId, "logs", currentCase.saleOrderId));
    });
    const mirrors = [];
    for (const ref of mirrorRefs) mirrors.push(await transaction.get(ref));
    const audit = { updatedAt: serverTimestamp(), updatedBy: getActorName(currentUser) };
    const values = {
      status: ANNUAL_MASK_STATUS.ORDER_COMPLETED,
      saleOrderStatus: "เสร็จสิ้น",
      saleOrderReceiptNo: safeText(order.receiptNo),
    };
    transaction.update(orderRef, { status: "เสร็จสิ้น", ...audit });
    transaction.update(caseRef, { ...values, ...audit });
    if (isCurrentLink) transaction.update(sourceRef, {
      "extendedProps.annualMaskPurchase.status": ANNUAL_MASK_STATUS.ORDER_COMPLETED,
      "extendedProps.annualMaskPurchase.updatedAt": serverTimestamp(),
    });
    mirrors.filter((snapshot) => snapshot.exists()).forEach((snapshot) => {
      transaction.update(snapshot.ref, { status: "เสร็จสิ้น", ...audit });
    });
    return values;
  });
};
